import type { StepperVariantProps } from './Stepper.variants';

type Size = NonNullable<StepperVariantProps['size']>;
type State = NonNullable<StepperVariantProps['state']>;

/**
 * Icon size inside the step circle per stepper size — from the Figma "check"
 * layers: 12 (small 24px) / 16 (medium 32px) / 22 (large 44px).
 */
export const STEPPER_ICON_SIZE_FOR: Record<Size, number> = {
  small: 12,
  medium: 16,
  large: 22,
};

/**
 * Resolved icon colour per (state, dark-mode). Mirrors the circle's `text-*`
 * foreground (stepperCircleVariants) — needed because `lucide-react-native`
 * takes `color` as a prop, not a className, so the CSS-var palette inversion
 * can't reach it. Web uses `currentColor` instead; this map is only consulted
 * on native. Dark = the mirrored shade (`-600`→`-400`, `-100`→`-900`; the
 * saturated `-500`s stay), matching the Figma dark frame's exported assets.
 */
const ICON_COLOR: Record<State, { light: string; dark: string }> = {
  // secondary-600 (#525252) / dark secondary-400 (#a1a1a1)
  idle: { light: '#525252', dark: '#a1a1a1' },
  // secondary-100 (#f5f5f5) / dark secondary-900 (#171717)
  active: { light: '#f5f5f5', dark: '#171717' },
  // green-500 — identical in both themes
  completed: { light: '#00c950', dark: '#00c950' },
  // red-500 — identical in both themes
  error: { light: '#fb2c36', dark: '#fb2c36' },
  // disabled = idle colours (the row itself gets opacity-50)
  disabled: { light: '#525252', dark: '#a1a1a1' },
};

export function resolveStepperIconColor(state: State, isDark: boolean): string {
  const pair = ICON_COLOR[state];
  return isDark ? pair.dark : pair.light;
}
